import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import startOfDay from 'date-fns/startOfDay'
import { usePageTitle } from '../../../actions/app'
import * as CreditsActions from '../../../actions/credits'
import { useAppDispatch } from '../../../actions/types'
import { Button, Centered, Column, Confirmation, Loader, Padding, SearchInput, Table } from '../../../components'
import { colors, Routes } from '../../../constants'
import { useAppSelector } from '../../../reducers'
import * as CreditsSelectors from '../../../reducers/credits'
import type { Credit } from '../../../types'
import { ArrayUtils } from '../../../utils'
import CreditForm from './CreditForm/CreditForm'
import creditTableDisplay from './Credits.table'
import useCreditParams from './useCreditParams'
import { useSize } from '../../../components/utils'

export type ExtendedCredit = Credit & { selected: boolean }

export default function Credits() {
    const dispatch = useAppDispatch()
    const isWorking = useAppSelector(CreditsSelectors.isWorking)
    const isLoadingSelectedCredit = useAppSelector(CreditsSelectors.isLoadingSelectedCredit)
    const credits = useAppSelector(CreditsSelectors.selectCredits)
    const selectedCredit = useAppSelector(CreditsSelectors.selectedCredit)
    const { t } = useTranslation()
    const history = useHistory()
    const { isMobile } = useSize()
    const { estate, creditId } = useCreditParams()

    usePageTitle(creditId && selectedCredit ? t('credit-title') : t('credits-title'))

    const formRef = React.useRef<HTMLDivElement>(null)

    const [bulkSelectedCredits, setBulkSelectedCredits] = React.useState<number[]>([])
    const [isCreatingNewCredit, setIsCreatingNewCredit] = React.useState(false)
    const [searchTerm, setSearchTerm] = React.useState('')
    const today = React.useMemo(() => startOfDay(new Date()), [])

    React.useEffect(() => {
        dispatch(CreditsActions.fetchCredits())
    }, [dispatch, estate])

    React.useEffect(() => {
        if (creditId) {
            setIsCreatingNewCredit(false)
            dispatch(CreditsActions.fetchCredit(creditId))

            if (isMobile) {
                formRef.current?.scrollIntoView()
            }
        } else if (isMobile) {
            window.scrollTo(0, 0)
        }
    }, [dispatch, estate, creditId, isMobile])

    const filteredCredits = React.useMemo(() => {
        const normalizedTerm = searchTerm.trim().toLowerCase()
        const sortByDateStartAsc = (list: Credit[]) =>
            [...list].sort((a, b) => a.start_date.getTime() - b.start_date.getTime())

        if (normalizedTerm.length >= 3) {
            return sortByDateStartAsc(credits.filter(credit => {
                const matchesType = credit.type.toLowerCase().includes(normalizedTerm)
                const matchesSku = credit.skus.some(sku => sku.toLowerCase().includes(normalizedTerm))
                return matchesType || matchesSku
            }))
        }

        return sortByDateStartAsc(credits.filter(credit => {
            const start = startOfDay(credit.start_date)
            const end = startOfDay(credit.end_date)
            return (start <= today && end >= today) || start > today
        }))
    }, [credits, searchTerm, today])

    const extendedCredits: ExtendedCredit[] = React.useMemo(() => {
        return filteredCredits.map(credit => {
            return {
                ...credit,
                selected: bulkSelectedCredits.includes(credit.id),
            }
        })
    }, [filteredCredits, bulkSelectedCredits])

    if (isWorking) {
        return (
            <Padding top>
                <Centered horizontal>
                    <Loader />
                </Centered>
            </Padding>
        )
    }

    const onClickRow = (clickedCreditId?: number) => {
        if (!clickedCreditId) {
            history.replace(
                Routes.buildRoute(Routes.SELF_SERVE_CREDITS, { estate }),
            )
            return
        }

        const path = Routes.buildRoute(
            Routes.SELF_SERVE_CREDITS,
            { estate, credit: clickedCreditId },
        )

        if (isMobile) {
            history.push(path)
        } else {
            history.replace(path)
        }
    }

    const onSelectCredit = (id: number, isSelected: boolean) => {
        setBulkSelectedCredits(prevSelection => {
            const itemIndex = prevSelection.findIndex(selectedId => selectedId === id)
            const isAlreadyIncluded = itemIndex > -1

            if (isSelected && !isAlreadyIncluded) {
                return [ ...prevSelection, id ]
            } else if (!isSelected && isAlreadyIncluded) {
                return ArrayUtils.removeItem(prevSelection, itemIndex)
            }

            return prevSelection
        })
    }

    const onDeleteCredits = () => {
        const fn = async () => {
            const isDeletingSelected = !!creditId && bulkSelectedCredits.includes(creditId)

            await Promise.all(bulkSelectedCredits.map(async creditId => {
                await dispatch(CreditsActions.deleteCredit(creditId))
            }))

            if (isDeletingSelected) {
                history.replace(
                    Routes.buildRoute(Routes.SELF_SERVE_CREDITS, { estate })
                )
                dispatch(CreditsActions.fetchCredits())
            } else {
                dispatch(CreditsActions.fetchCredits())
            }

            setBulkSelectedCredits([])
        }

        fn()
    }

    const onClickCreate = () => {
        setIsCreatingNewCredit(true)
        onClickRow()

        if (isMobile) {
            formRef.current?.scrollIntoView()
        }
    }

    return (
        <>
            <Padding>
                <Button
                    theme="forestGreen"
                    onClick={onClickCreate}
                >
                    {t('credit-create')}
                </Button>
                <Padding top>
                    <div style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', marginBottom: '10px' }}>
                        <SearchInput
                            width="280px"
                            value={searchTerm}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
                        />
                        {searchTerm.length > 0 && (
                            <button
                                aria-label="Clear search"
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    color: colors.greydark,
                                    padding: 0,
                                    height: '16px',
                                    width: '16px',
                                    lineHeight: '16px',
                                }}
                            >
                                x
                            </button>
                        )}
                    </div>
                </Padding>
                <Confirmation
                    cancel={t('cancel')}
                    confirmation={t('credit-remove-confirm-body', {
                        count: bulkSelectedCredits.length,
                    })}
                    header={t('credit-remove-confirm-header')}
                    onCancel={() => null}
                    onConfirm={onDeleteCredits}
                    ok={t('credit-remove-confirm-button')}
                    theme="danger"
                >
                    <Button
                        theme={bulkSelectedCredits.length > 0 ? 'danger' : 'disabled'}
                        disabled={bulkSelectedCredits.length === 0}
                    >
                        {bulkSelectedCredits.length === 0 && t('credit-remove-count-none')}
                        {bulkSelectedCredits.length > 0 && t('credit-remove-count', { count: bulkSelectedCredits.length })}
                    </Button>
                </Confirmation>
            </Padding>
            <Column width={isMobile ? '100%' : '50%'}>
                <Table
                    data={extendedCredits}
                    display={
                        creditTableDisplay({ onSelectCredit, t })
                    }
                    keyField="id"
                    onClickRow={onClickRow}
                    hideDeviceButtons
                    selectedItem={creditId}
                />
            </Column>
            <Column
                ref={formRef}
                width={isMobile ? '100%' : '50%'}
                color={colors.bluedarkest}
            >
                {!selectedCredit && isLoadingSelectedCredit && (
                    <Padding top>
                        <Centered horizontal>
                            <Loader />
                        </Centered>
                    </Padding>
                )}
                {(isCreatingNewCredit || !selectedCredit) && <CreditForm />}
                {!isCreatingNewCredit && selectedCredit && <CreditForm credit={selectedCredit} />}
            </Column>
        </>
    )
}
